/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useActionData, useFetcher } from '@remix-run/react';
import { withZod } from '@remix-validated-form/with-zod';
import PasswordValidation from '~/components/password-validation';
import { TermsAndConditionsDialog } from '~/components/terms-condition-dialog';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Dialog, DialogContent } from '~/components/ui/dialog';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { PasswordInput } from '~/components/ui/password-input';
import { PhoneInput } from '~/components/ui/phone-input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '~/components/ui/select';
import { useToast } from '~/components/ui/use-toast';
import {
  UserCreateAddressInput,
  userCreateAddressValidator,
  UserCreateInput,
  userCreateValidator,
} from '~/services/models/users';
import { LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { getCountryCallingCode } from 'react-phone-number-input';
import { ValidatedForm } from 'remix-validated-form';

const userCreateValidation = withZod(userCreateValidator);
const userCreateAddressValidation = withZod(userCreateAddressValidator);

interface RegistrationProps {
  openRegisterDialog: boolean;
  setOpenRegisterDialog: (value: boolean) => void;
  onSwitchToLogin: () => void;
  layout?: 'default' | 'footer';
}

interface FetcherData {
  status: string;
  action?: string;
  error?: string;
}

export default function Registration({
  openRegisterDialog,
  setOpenRegisterDialog,
  onSwitchToLogin,
  layout = 'default',
}: RegistrationProps) {
  const [step, setStep] = useState<number>(1);
  const [selectedCountry, setSelectedCountry] = useState<string>();
  const [selectedState, setSelectedState] = useState<string>();
  const [termsChecked, setTermsChecked] = useState(false);
  const [openTermsAndConditionDialog, setOpenTermsAndConditionDialog] = useState(false);

  const fetcher = useFetcher();
  const actionData = useActionData<any>();
  const toast = useToast();

  const registerCreateUserForm = useForm<UserCreateInput>({
    resolver: zodResolver(userCreateValidator),
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '+60',
      email: '',
      password: '',
    },
  });

  const registerCreateAddressForm = useForm<UserCreateAddressInput>({
    resolver: zodResolver(userCreateAddressValidator),
    defaultValues: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postcode: '',
      country: '',
    },
  });

  const handleTermsChange = () => {
    setTermsChecked(!termsChecked);
  };

  useEffect(() => {
    if (!openRegisterDialog) {
      setStep(1);
    }
  }, [openRegisterDialog]);

  useEffect(() => {
    const fetcherData = fetcher.data as FetcherData;
    if (fetcherData?.status === 'success' && fetcherData?.action === 'register-user') {
      toast.toast({
        title: 'Success',
        description: 'Your account has been successfully registered!',
      });

      setStep(2);
    } else if (fetcherData?.status === 'success' && fetcherData?.action === 'register-address') {
      toast.toast({
        title: 'Success',
        description: 'Your address has been successfully registered!',
      });

      setOpenRegisterDialog(false);
    }

    if (fetcherData?.error) {
      toast.toast({
        variant: 'destructive',
        title: 'Error',
        description:
          typeof actionData?.error === 'string' ? actionData?.error : 'Something went wrong. Please try again',
      });
    }
  }, [fetcher.data]);

  const createAccount = (
    <>
      <ValidatedForm validator={userCreateValidation} method="post" action="/api/register-user" fetcher={fetcher}>
        <Form {...registerCreateUserForm}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col items-center justify-center">
              <p className="text-xl font-medium text-black">Create Account</p>
              <p className="text-sm font-normal text-slate-500">Sign up to register for this auction</p>
            </div>
            <div className="my-4 flex flex-col gap-2">
              <div className="flex flex-row gap-4">
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <FormField
                    control={registerCreateUserForm.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="mb-2">
                        <FormLabel>First Name</FormLabel>
                        <Input placeholder="Enter your first name" {...field} />
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <FormField
                    control={registerCreateUserForm.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="mb-2">
                        <FormLabel>Last Name</FormLabel>
                        <Input placeholder="Enter your last name" {...field} />
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <div className="w-full items-center gap-1.5">
                <FormField
                  control={registerCreateUserForm.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Phone number</FormLabel>
                      <PhoneInput
                        placeholder="Enter your phone number"
                        onCountryChange={(code) => {
                          if (code) {
                            field.onChange('+' + getCountryCallingCode(code) + field.value);
                            return code;
                          }
                        }}
                        required
                        {...field}
                      />
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
              <div className="w-full items-center gap-1.5">
                <FormField
                  control={registerCreateUserForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Email</FormLabel>
                      <Input placeholder="Enter your email" {...field} />
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
              <div className="w-full items-center gap-1.5">
                <FormField
                  control={registerCreateUserForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Password</FormLabel>
                      <PasswordInput placeholder="Enter your password" {...field} />
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
              <PasswordValidation control={registerCreateUserForm.control} />
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox id="terms" onCheckedChange={handleTermsChange} checked={termsChecked} />
                <label
                  htmlFor="terms"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I agree to the{' '}
                  <a className="cursor-pointer text-blue-600" onClick={() => setOpenTermsAndConditionDialog(true)}>
                    terms & conditions
                  </a>
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="reminder" />
                <label
                  htmlFor="reminder"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Please send me auction reminders via email
                </label>
              </div>
            </div>
            <Button
              variant={'primary'}
              onClick={() => {
                registerCreateUserForm.trigger();
              }}
              disabled={fetcher.state === 'submitting' || !termsChecked}
            >
              {fetcher.state === 'submitting' && <LoaderCircle className="mr-2 animate-spin" />}
              Join
            </Button>
            <div className="flex flex-row items-center justify-center gap-2 text-sm font-semibold">
              <p className="text-slate-700">Already have an account?</p>
              <Button variant="link" className="px-0 text-blue-600" onClick={onSwitchToLogin}>
                Sign In
              </Button>
            </div>
            <p className="text-xs font-normal text-slate-500">
              By continuing you agree to the <span className="text-blue-600">terms of service</span> and{' '}
              <span className="text-blue-600">privacy policy</span>. We may send you communications; you can set your
              preferences in your account.
            </p>
          </div>
        </Form>
      </ValidatedForm>
      <TermsAndConditionsDialog
        open={() => openTermsAndConditionDialog}
        onOpenChange={setOpenTermsAndConditionDialog}
      />
    </>
  );

  const createPaymentMethod = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center justify-center">
        <p className="text-xl font-medium text-black">Add Payment Method</p>
        <p className="text-pretty text-center text-sm font-normal text-slate-500">
          You won&apos;t be charged unless you win. This information will help you signal to auction houses that
          you&apos;re able to pay for the items you win.
        </p>
      </div>
      <div className="my-4 flex flex-col gap-2">
        <div className="w-full items-center gap-1.5">
          <Label htmlFor="cardName">Name on card</Label>
          <Input type="text" id="cardName" placeholder="Enter name on card" />
        </div>
        <div className="w-full items-center gap-1.5">
          <Label htmlFor="cardNo">Card number</Label>
          <Input type="text" id="cardNo" placeholder="Enter card number" />
        </div>
        <div className="flex flex-row gap-4">
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="expiration">Expiration</Label>
            <Input type="text" id="expiration" placeholder="Password" />
          </div>
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="ccv">CVV</Label>
            <Input type="text" id="ccv" placeholder="Enter CVV" />
          </div>
        </div>
        <div className="w-full items-center gap-1.5">
          <Label htmlFor="billingAddress">Billing Address</Label>
          <Input type="text" id="billingAddress" placeholder="Enter billing address" />
        </div>
        <div className="w-full items-center gap-1.5">
          <Label htmlFor="apt">Apt, Suite, etc (optional)</Label>
          <Input type="text" id="apt" placeholder="Enter apt, suite, etc" />
        </div>
        <div className="flex flex-row gap-4">
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <div className="col-span-1">
              <Label className="text-xs font-medium text-slate-900">Country</Label>
              <Select
                name="country"
                onValueChange={(value) => {
                  setSelectedCountry(value);
                }}
              >
                <SelectTrigger>{selectedCountry ? selectedCountry : 'Select Option'}</SelectTrigger>
                <SelectContent>
                  <SelectItem value={'Malaysia'}>Malaysia</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <div className="col-span-1">
              <Label className="text-xs font-medium text-slate-900">State/Province</Label>
              <Select
                name="state"
                onValueChange={(value) => {
                  setSelectedState(value);
                }}
              >
                <SelectTrigger>{selectedState ? selectedState : 'Select Option'}</SelectTrigger>
                <SelectContent>
                  <SelectItem value={'Kuala Lumpur'}>Kuala Lumpur</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-4">
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="city">City</Label>
            <Input type="text" id="city" placeholder="Enter city" />
          </div>
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="code">Postal Code</Label>
            <Input type="text" id="code" placeholder="Enter postal code" />
          </div>
        </div>
      </div>
      <Button variant={'primary'} onClick={() => setStep(3)}>
        Save and Continue
      </Button>

      <Button
        variant={'ghost'}
        onClick={() => {
          setOpenRegisterDialog(false);
        }}
      >
        Skip for now
      </Button>
    </div>
  );

  const createProfile = (
    <ValidatedForm
      validator={userCreateAddressValidation}
      method="post"
      action="/api/register-address"
      fetcher={fetcher}
    >
      <Form {...registerCreateAddressForm}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center justify-center">
            <p className="text-xl font-medium text-black">You’re almost there!</p>
            <p className="text-pretty text-center text-sm font-normal text-slate-500">
              Complete your profile to start bidding
            </p>
          </div>
          <div className="my-4 flex flex-col gap-2">
            <div className="flex flex-row gap-4">
              <div className="grid w-full max-w-sm items-center gap-1.5">
                <div className="col-span-1">
                  <FormField
                    control={registerCreateAddressForm.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem className="mb-2">
                        <FormLabel>Country</FormLabel>
                        <Select
                          {...field}
                          onValueChange={(value) => {
                            setSelectedCountry(value);
                            registerCreateAddressForm.setValue('country', value);
                          }}
                        >
                          <SelectTrigger>{selectedCountry ? selectedCountry : 'Select Option'}</SelectTrigger>
                          <SelectContent>
                            <SelectItem value={'Malaysia'}>Malaysia</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <div className="grid w-full max-w-sm items-center gap-1.5">
                <div className="col-span-1">
                  <FormField
                    control={registerCreateAddressForm.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem className="mb-2">
                        <FormLabel>State/Province</FormLabel>
                        <Select
                          {...field}
                          onValueChange={(value) => {
                            setSelectedState(value);
                            registerCreateAddressForm.setValue('state', value);
                          }}
                        >
                          <SelectTrigger>{selectedState ? selectedState : 'Select Option'}</SelectTrigger>
                          <SelectContent>
                            <SelectItem value={'Johor'}>Johor</SelectItem>
                            <SelectItem value={'Kedah'}>Kedah</SelectItem>
                            <SelectItem value={'Kelantan'}>Kelantan</SelectItem>
                            <SelectItem value={'Kuala Lumpur'}>Kuala Lumpur</SelectItem>
                            <SelectItem value={'Labuan'}>Labuan</SelectItem>
                            <SelectItem value={'Melaka'}>Melaka</SelectItem>
                            <SelectItem value={'Negeri Sembilan'}>Negeri Sembilan</SelectItem>
                            <SelectItem value={'Pahang'}>Pahang</SelectItem>
                            <SelectItem value={'Penang'}>Penang</SelectItem>
                            <SelectItem value={'Perak'}>Perak</SelectItem>
                            <SelectItem value={'Perlis'}>Perlis</SelectItem>
                            <SelectItem value={'Putrajaya'}>Putrajaya</SelectItem>
                            <SelectItem value={'Sabah'}>Sabah</SelectItem>
                            <SelectItem value={'Sarawak'}>Sarawak</SelectItem>
                            <SelectItem value={'Selangor'}>Selangor</SelectItem>
                            <SelectItem value={'Terengganu'}>Terengganu</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="w-full items-center gap-1.5">
              <FormField
                control={registerCreateAddressForm.control}
                name="line1"
                render={({ field }) => (
                  <FormItem className="mb-2">
                    <FormLabel>Shipping Address</FormLabel>
                    <Input placeholder="Enter shipping address" {...field} />
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-row gap-4">
              <div className="grid w-full max-w-sm items-center gap-1.5">
                <FormField
                  control={registerCreateAddressForm.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>City</FormLabel>
                      <Input placeholder="Enter city" {...field} />
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid w-full max-w-sm items-center gap-1.5">
                <FormField
                  control={registerCreateAddressForm.control}
                  name="postcode"
                  render={({ field }) => (
                    <FormItem className="mb-2">
                      <FormLabel>Postal Code</FormLabel>
                      <Input placeholder="Enter postal code" {...field} />
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
          <Button
            variant={'primary'}
            onClick={() => {
              registerCreateAddressForm.trigger();
            }}
            disabled={fetcher.state === 'submitting'}
          >
            {fetcher.state === 'submitting' && <LoaderCircle className="mr-2 animate-spin" />}
            Save and Continue
          </Button>

          <Button
            variant={'ghost'}
            onClick={() => {
              setOpenRegisterDialog(false);
            }}
          >
            Skip for now
          </Button>
        </div>
      </Form>
    </ValidatedForm>
  );

  return (
    <Dialog open={openRegisterDialog} onOpenChange={setOpenRegisterDialog}>
      {layout === 'default' ? (
        <>
          <Button variant="primary" className="hidden lg:flex" onClick={() => setOpenRegisterDialog(true)}>
            Register
          </Button>
          <Button variant="ghost" className="text-md lg:hidden" onClick={() => setOpenRegisterDialog(true)}>
            Register
          </Button>
        </>
      ) : (
        <p className="cursor-pointer hover:underline" onClick={() => setOpenRegisterDialog(true)}>
          Register
        </p>
      )}

      <DialogContent className="max-h-screen overflow-y-scroll sm:max-w-[700px]">
        {step === 1 ? createAccount : step === 2 ? createPaymentMethod : createProfile}
      </DialogContent>
    </Dialog>
  );
}
